<template>
  <div
    v-if="props.areGroupingModesFloating"
    class="space" />
  <div
    class="grouping-modes-container"
    :class="{ 'anchored-top': props.areGroupingModesFloating }">
    <div
      class="section-separator"
      :style="marginsFormatted">
      <div
        v-if="props.showSectionSeparatorLine"
        class="line" />
      <div class="btn-group grouping-modes">
        <button
          v-for="mode in groupingModes"
          :key="mode.name"
          class="btn"
          :class="{
            selected: selectedGroupingMode?.name === mode?.name,
            'btn-lg': !props.small,
            'btn-sm': props.small,
          }"
          :style="buttonStyleFormatted"
          :disabled="props.disabled"
          @click="changeMode(mode)">
          <component :is="mode.icon" />
          {{ mode.name }}
        </button>
      </div>
      <div
        v-if="props.showSectionSeparatorLine"
        class="line" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  interface GroupingMode {
    name: string;
    collection?: string;
    dimension?: string;
    chartType?: string;
    icon: unknown;
  }

  interface Margins {
    bottom: number;
    top: number;
  }

  interface Props {
    areGroupingModesFloating: boolean;
    groupingModes: GroupingMode[];
    showSectionSeparatorLine: boolean;
    defaultSelectedGroupingMode?: GroupingMode;
    margins?: Margins;
    uppercase: boolean;
    padding?: string;
    disabled?: boolean;
    small?: true;
    fontSize?: string;
  }

  const defaultMargins: Margins = {
    top: 2.4,
    bottom: 3.2,
  };

  const emit = defineEmits(['modeChanged']);

  const props = defineProps<Props>();
  const selectedGroupingMode = ref<GroupingMode>(null);

  const marginsFormatted = computed<string>(() => {
    const { top, bottom } = props.margins || defaultMargins;
    return `margin-top: ${top}rem; margin-bottom: ${bottom}rem;`;
  });
  const paddingFormatted = computed<string>(() => {
    const defaultPadding = '1.2rem 2.4rem';
    return `padding: ${props.padding || defaultPadding};`;
  });
  const fontFormatted = computed<string>(() => {
    const defaultFontSize = '1.1em';
    const fontSize = `font-size: ${props.fontSize || defaultFontSize};`;
    const textTransform = `text-transform: ${props.uppercase === false ? 'none' : 'uppercase'}`;
    return `${fontSize} ${textTransform}`;
  });
  const buttonStyleFormatted = computed<string>(() => {
    let style = '';
    if (paddingFormatted.value) style += paddingFormatted.value;
    if (fontFormatted.value) style += fontFormatted.value;
    return style;
  });

  function changeMode(mode: GroupingMode): void {
    if (props.disabled) {
      return;
    }
    selectedGroupingMode.value = mode;
    emit('modeChanged', mode);
  }

  watch(
    () => props.groupingModes,
    (modes) => {
      const selected = modes.find((mode) => mode?.selected);
      if (selected) {
        changeMode(selected);
      }
    },
    { immediate: true },
  );

  watch(
    () => props.defaultSelectedGroupingMode,
    (mode) => {
      selectedGroupingMode.value = mode || props.groupingModes[0];
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .space {
    width: 100%;
    height: 10.2rem;
  }

  .grouping-modes {
    .btn {
      font-weight: 600 !important;

      &:hover,
      &.selected {
        svg {
          stroke: var(--actionsColorReverse);
        }
      }

      svg {
        margin-right: 0.4rem;
        width: 1.4rem;
        height: 1.4rem;
        stroke: var(--actionsColor);
      }
    }
  }

  #app {
    .main-content {
      &.expanded {
        .anchored-top {
          width: calc(100% - 6.4rem);
        }
      }

      .anchored-top {
        position: fixed;
        top: 14rem;
        z-index: 1;
        width: calc(100% - 23rem);
        background: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0));
        margin: 0.5rem -0.9rem;

        .line {
          opacity: 0;
        }

        .btn-group {
          box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.25);
        }
      }

      .section-separator {
        display: flex;
        align-items: center;
        justify-content: center;
        text-transform: uppercase;
        font-weight: 700;

        .line {
          border-top: 0.1rem dotted var(--actionsColorFaded80);
          flex: 1 0 3%;
          margin: auto 2rem;
        }

        .btn-group {
          background: none;
          border-radius: 1rem;
          box-shadow: $small-shadow;

          .btn {
            color: var(--actionsColor);
            border: solid 0.1rem var(--actionsColor);
            background: var(--actionsColorReverse);
            transition: all 1s cubic-bezier(0.2, 1, 0.2, 1);
            font-weight: 300;
            font-size: 1.1em;

            &:focus {
              outline: none;
            }

            &:first-of-type {
              border-radius: 1rem 0 0 1rem;
            }

            &:last-of-type {
              border-radius: 0 1rem 1rem 0;
            }

            &.selected,
            &:hover {
              transition: all 1s cubic-bezier(0.2, 1, 0.2, 1);
              color: var(--actionsColorReverse);

              svg {
                fill: var(--actionsColorReverse);
              }
            }

            &.selected {
              font-weight: 600;
              background-color: var(--actionsColor);
            }

            &:hover {
              background-color: var(--actionsColorLight);
            }
          }
        }
      }
    }
  }
</style>
